import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { List } from "immutable";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AuthService } from "../../../../../../auth/auth.service";
import { ListItem } from "../../../../../../shared/list/list-item";
import { NumberHelper } from "../../../../../../utilities/contracts/number-helper";
import { ChaseDetailStateService } from "../../../../member/chase-detail/chase-detail-state.service";
import { ChaseDetailService } from "../../../../member/chase-detail/chase-detail.service";
import { ProductType } from "../../../../project/new-project/product-type.enum";
import { DirectoryUserRole } from "../../../../retrieval/directory-user-role";
import { AuditQueryChartService } from "../../audit-query-quality/audit-query-chart/audit-query-chart.service";
import { AuditQueryChartRiskService } from "../../audit-query-risk/audit-query-chart-risk/audit-query-chart-risk.service";
import { AuditQueryHeaderService } from "./audit-query-header.service";

@Component({
  selector: "app-audit-query-header",
  templateUrl: "./audit-query-header.component.html",
  styleUrls: ["./audit-query-header.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditQueryHeaderComponent implements OnInit, OnDestroy {
  @Output() currentTab = new EventEmitter<string>();
  @Input() stateName = "";
  private unsubscribe = new Subject();
  selectedTab = "auditquery";
  chaseDetails = List<ListItem>();
  qualityAuditChaseDetails: any = null;
  riskAuditChaseDetails: any = null;
  moveToPreviousUrl = false;
  chaseId: number = null;
  isAssignToChaseModalVisible = false;
  selectedChases: number[] = [];
  private isHedisProject = false;

  constructor(
    private auditQueryHeaderService: AuditQueryHeaderService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly authService: AuthService,
    private readonly chaseDetailStateService: ChaseDetailStateService,
    private readonly chaseDetailService: ChaseDetailService,
    private readonly auditQueryChartService: AuditQueryChartService,
    private readonly auditQueryChartRiskService: AuditQueryChartRiskService
  ) { }

  ngOnInit() {
    this.auditQueryHeaderService.changeTab(this.selectedTab);
    this.auditQueryHeaderService.selectedTab
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(data => {
        this.toggleTab(data);
      });

    this.auditQueryHeaderService.setChaseDetails([]);
    this.auditQueryHeaderService.chaseDetails
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(items => {
        this.chaseDetails = List(items);
        this.changeDetector.markForCheck();
      });

    this.auditQueryChartService.auditChaseDetails
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(data => {
        if (data != null && data.chaseId) {
          this.qualityAuditChaseDetails = data;
          this.chaseId = data.chaseId;
          this.isHedisProject = true;
          this.changeDetector.markForCheck();
        } else if (data == null) {
          this.qualityAuditChaseDetails = null;
          this.chaseId = null;
          this.isHedisProject = false;
          this.changeDetector.markForCheck();
        }
      });

    this.auditQueryChartRiskService.auditChaseDetails
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(data => {
        if (data != null && data.chaseId) {
          this.riskAuditChaseDetails = data;
          this.isHedisProject = false;
          this.changeDetector.markForCheck();
        } else if (data == null) {
          this.riskAuditChaseDetails = null;
          this.chaseId = null;
          this.isHedisProject = false;
          this.changeDetector.markForCheck();
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  toggleTab(tab: string): void {
    this.selectedTab = (tab === "") ? this.selectedTab : tab;
    this.currentTab.emit(this.selectedTab);
    this.moveToPreviousUrl = tab === "auditquery";
  }

  get chaseDetailsVisible(): boolean {
    return (this.chaseDetails && this.selectedTab === "chart");
  }

  get isHedisNextGen(): boolean {
    return this.qualityAuditChaseDetails?.templateId >= ProductType.FULL_WORKFLOW_V2025_NEWGEN;
  }

  get headerText(): string {
    return `Audit Query`;
  }

  get hasChaseAssignAccess(): boolean {
    const roles = [
      DirectoryUserRole.ClinicalLead,
      DirectoryUserRole.MRRManager,
      DirectoryUserRole.OverreadManager,
      DirectoryUserRole.ClientOverreadManager,
      DirectoryUserRole.AuditLead,
      DirectoryUserRole.AuditManager,
      DirectoryUserRole.Admin,
    ];

    const state = this.chaseDetailStateService.state.value;
    if (state && state.workflowStatusName === "Overread2") {
      roles.push(DirectoryUserRole.ClientLead);
    }
    return this.authService.user.directoryRoleIds.some(roleId => roles.includes(roleId));
  }

  get showAssignButton(): boolean {
    return this.hasChaseAssignAccess
      && this.chaseDetailsVisible
      && this.isHedisProject
      && !this.isHedisNextGen;
  }

  openAssignModal(): void {
    this.selectedChases = [this.chaseId];
    this.isAssignToChaseModalVisible = true;
  }

  closeModal(): void {
    this.selectedChases = [];
  }

  updateChaseAssignedData(): void {
    if (this.chaseId != null && NumberHelper.isGreaterThan(this.chaseId, 0)) {
      this.chaseDetailService.getChaseDetail(this.chaseId).subscribe(chaseDetail => {
        this.chaseDetailService.chaseDetailsChange.next(chaseDetail.keyValueItems);
        this.chaseDetailStateService.setData({
          ...chaseDetail,
        });
        this.changeDetector.markForCheck();
      });
    }
  }

}
